import { useCallback } from 'react';
import { useWeb5Context } from 'lib/Web5Context';
import { fieldOperators, operatorsToBeShownOnlyInCrossFilters, DIVIDER } from '../operators';

const useAdvancedFilters = ({
    operators,
    crossOperators,
    getAllowedOperators = () => {},
    getFieldsWithoutAdvancedFilters = () => {},
    getHasAdvancedFilters = () => {},
}) => {
    const { t } = useWeb5Context();

    const getFieldOperator = useCallback(
        (field, crossEntity) => {
            const currentOperators =
                !!crossEntity && !field.isFakeCrossFilter
                    ? crossOperators?.[crossEntity] || {}
                    : operators;
            const { id } = field;
            const operator = currentOperators && currentOperators[id];
            return operator ? operator.value : undefined;
        },
        [operators, crossOperators],
    );

    const getOperatorValues = useCallback(
        (field, current, crossEntity) => {
            const valuesObj = fieldOperators[field];
            if (!valuesObj) return { options: [], value: null };

            const { operators, default: defaultValue } = valuesObj;
            const allowedOperators = getAllowedOperators(crossEntity)?.[field];

            const valuesArr = Object.entries(operators).reduce((arr, [key, value]) => {
                // Filters the advanced filters meant to be shown only when in crossfilters context (crossEntity = null)
                if (!crossEntity && operatorsToBeShownOnlyInCrossFilters.includes(value))
                    return arr;
                if (
                    (allowedOperators && (allowedOperators.includes(value) || value === DIVIDER)) ||
                    !allowedOperators
                ) {
                    const newValue =
                        value === DIVIDER
                            ? { divider: true }
                            : {
                                  label: t(key),
                                  value,
                              };

                    return [...arr, newValue];
                } else {
                    return arr;
                }
            }, []);

            const currentValue = current || defaultValue;
            const value = valuesArr.filter((item) => item.value === currentValue)[0];

            const operatorDefaultValue = valuesArr.filter((item) => item.value === defaultValue)[0];

            return { options: valuesArr, value, defaultValue: operatorDefaultValue };
        },
        [getAllowedOperators, t],
    );

    const hasAdvancedFilters = useCallback(
        ({ id, dataType, crossEntity, avoidAdvancedFilter = false }) => {
            const allowedOperators = getAllowedOperators(crossEntity);
            const advancedFilters = getHasAdvancedFilters(crossEntity);
            const fieldsWithoutAdvancedFilters = getFieldsWithoutAdvancedFilters(crossEntity);

            if (avoidAdvancedFilter) return false;

            if (fieldsWithoutAdvancedFilters?.includes(id)) return false;

            return advancedFilters && (!allowedOperators || allowedOperators[dataType]);
        },
        [getAllowedOperators, getFieldsWithoutAdvancedFilters, getHasAdvancedFilters],
    );

    return {
        getFieldOperator,
        getOperatorValues,
        hasAdvancedFilters,
    };
};

export default useAdvancedFilters;
